<template>
  <div class="min-h-screen bg-gradient-to-br from-teal-100 to-white flex flex-col items-center py-8">
    <div class="w-full max-w-2xl bg-white rounded-2xl shadow-lg border border-teal-100 p-6">
      <div class="flex flex-col items-center mb-6">
        <i class="fa fa-receipt text-4xl text-teal-600 mb-2"></i>
        <h1 class="text-2xl font-bold text-teal-800 text-center">Detalle de tu compra</h1>
        <p class="text-gray-500 text-center">Esta es la información de tu pedido realizado en <strong>{{ order.organization?.name || 'nuestra tienda' }}</strong>.</p>
      </div>
      <!-- Cliente -->
      <div class="mb-4">
        <div class="flex items-center gap-2 mb-1">
          <i class="fa fa-user-circle text-teal-500"></i>
          <span class="font-semibold">Cliente:</span>
          <span>{{ order.client.name }}</span>
        </div>
        <div class="flex items-center gap-2 mb-1">
          <i class="fa fa-address-card text-gray-400"></i>
          <span>{{ order.client.identification }}</span>
        </div>
        <div class="flex items-center gap-2 mb-1">
          <i class="fa fa-envelope text-gray-400"></i>
          <span>{{ order.client.email || '-' }}</span>
        </div>
      </div>
      <!-- Productos -->
      <div class="mb-6">
        <h2 class="font-semibold text-lg mb-2 flex items-center gap-2">
          <i class="fa fa-boxes text-teal-500"></i> Detalle de productos y servicios
        </h2>
        <table class="w-full text-sm mb-2">
          <thead class="bg-gray-50">
            <tr>
              <th class="p-2 text-left">Producto/Servicio</th>
              <th class="p-2 text-left">Descripción</th>
              <th class="p-2 text-center">Cantidad</th>
              <th class="p-2 text-right">Precio</th>
              <th class="p-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(product, idx) in order.products" :key="idx" class="border-b">
              <td class="p-2">{{ product.product.name || '-' }}</td>
              <td class="p-2 text-xs text-gray-600">{{ product.product.description || '-' }}</td>
              <td class="p-2 text-center">{{ product.quantity }}</td>
              <td class="p-2 text-right">${{ Number(product.price).toFixed(2) }}</td>
              <td class="p-2 text-right">${{ (product.price * product.quantity).toFixed(2) }}</td>
            </tr>
          </tbody>
        </table>
        <div class="flex flex-col sm:flex-row gap-4 justify-between mt-4">
          <div class="flex items-center gap-2">
            <i class="fa fa-tag text-gray-400"></i>
            <span class="font-semibold">Descuento:</span>
            <span v-if="order.discount && order.discount.value">{{ order.discount.type === 'amount' ? ('$' + Number(order.discount.value).toFixed(2)) : (Number(order.discount.value) + '%') }}</span>
            <span v-else>-</span>
          </div>
          <div class="flex items-center gap-2">
            <i class="fa fa-coins text-gray-400"></i>
            <span class="font-semibold">Total:</span>
            <span class="text-teal-700 text-lg font-bold">${{ getTotal() }}</span>
          </div>
        </div>
      </div>
      <!-- Pagos -->
      <div class="mb-6">
        <h2 class="font-semibold text-lg mb-2 flex items-center gap-2">
          <i class="fa fa-credit-card text-teal-500"></i> Pagos y abonos realizados
        </h2>
        <table v-if="order.pays && order.pays.length" class="w-full text-sm mb-2">
          <thead class="bg-gray-50">
            <tr>
              <th class="p-2 text-left">Fecha</th>
              <th class="p-2 text-left">Método</th>
              <th class="p-2 text-right">Monto</th>
              <th class="p-2 text-left">Referencia</th>
              <th class="p-2 text-left">Estatus</th>
              <th class="p-2 text-left">Voucher</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="pay in order.pays" :key="pay.id" class="border-b">
              <td class="p-2">{{ formatDate(pay.created_at) }}</td>
              <td class="p-2">{{ pay.method }}</td>
              <td class="p-2 text-right">${{ pay.amount }}</td>
              <td class="p-2">{{ pay.reference }}</td>
              <td class="p-2 capitalize">
                <span :class="{
                  'text-green-600 font-bold': pay.status === 'approve',
                  'text-yellow-600': pay.status === 'pending',
                  'text-red-600': pay.status === 'reject'
                }">
                  {{ pay.status === 'approve' ? 'Aprobado' : pay.status === 'pending' ? 'Pendiente' : 'Rechazado' }}
                </span>
              </td>
              <td class="p-2">
                <a v-if="pay.voucher" :href="`/storage/${pay.voucher}`" target="_blank">
                  <img :src="`/storage/${pay.voucher}`" alt="Voucher" class="h-10 rounded shadow border" />
                </a>
                <span v-else class="text-gray-400">-</span>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-else class="text-gray-400 py-2">No hay pagos registrados aún.</div>
      </div>
      <!-- Mensaje de compra pagada -->
      <div v-if="order.order_state === 'finish'" class="mb-6">
        <div class="bg-green-100 border border-green-300 text-green-800 rounded-lg p-4 flex flex-col items-center mb-3">
          <i class="fa fa-check-circle text-2xl mb-2"></i>
          <div class="font-bold text-lg">¡Compra pagada!</div>
          <div class="text-sm mb-2">Tu compra ha sido pagada exitosamente.</div>
          <a :href="`/orders/${order.id}/paid-support`" target="_blank" class="btn-blue px-4 py-2 rounded shadow flex items-center gap-2 mt-2">
            <i class="fa fa-file-pdf"></i> Descargar soporte de compra pagada (PDF)
          </a>
        </div>
      </div>
      <!-- Registrar nuevo pago -->
      <div v-if="order.order_state !== 'finish' && order.order_state !== 'cancel'" class="mb-6">
        <h2 class="font-semibold text-lg mb-2 flex items-center gap-2">
          <i class="fa fa-plus-circle text-teal-500"></i> Registrar un pago o abono
        </h2>
        <PaymentBox
          :quick="true"
          :order="order"
          :balance="order.total - order.balance"
          :payment_methods="paymentMethods"
          @success="onPaymentSuccess"
        />
        <div v-if="paymentMessage" class="mt-3 text-green-600 text-center">{{ paymentMessage }}</div>
      </div>
      <div class="text-center text-gray-400 text-xs mt-8">Esta página es solo para consulta y registro de pagos. Si tienes dudas, contacta a tu vendedor.</div>
      <div class="flex flex-col items-center justify-center mt-4">
        <span class="text-sm text-gray-400 font-semibold mb-1 flex items-center gap-2">
          Powered by
          <a targe="_blank" href="https://realgestion.lat"><img src="/logo.png" alt="Logo" class="h-12 w-auto opacity-90 inline-block align-middle" style="display:inline-block;vertical-align:middle;" /></a>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import PaymentBox from '@/Shared/PaymentBox.vue';

export default {
  components: { PaymentBox },
  props: {
    order: Object,
    paymentMethods: Object
  },
  data() {
    return {
      paymentMessage: ''
    }
  },
  methods: {
    onPaymentSuccess() {
      this.paymentMessage = '¡Pago registrado correctamente! Será validado por un administrador.';
      // Opcional: recargar pagos
      if (typeof this.fetchOrder === 'function') {
        this.fetchOrder();
      } else {
        window.location.reload();
      }
    },
    getTotal() {
      let total = this.order.total;
      if (this.order.discount && this.order.discount.value) {
        if (this.order.discount.type === 'amount') {
          total -= Number(this.order.discount.value);
        } else {
          total -= (total * Number(this.order.discount.value) / 100);
        }
      }
      return Number(total).toFixed(2);
    },
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('es-VE', { year: 'numeric', month: 'short', day: 'numeric' });
    },
    async registerPayment() {
      // Aquí deberías llamar a tu endpoint de backend para registrar el pago
      // Por ahora solo simula éxito
      this.paymentMessage = '¡Pago registrado exitosamente!';
      this.paymentForm = { method: '', amount: '', reference: '' };
      setTimeout(() => this.paymentMessage = '', 4000);
    }
  }
}
</script>

<style scoped>
body {
  background: #a2a2a2;
}
.btn-blue {
  @apply bg-teal-700 hover:bg-teal-800 text-white rounded-lg shadow transition-colors;
}
</style>
